import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Button, Input } from '@/presentation/components/common';
import { useToast } from '@/presentation/contexts/ToastContext';
import { useAuth } from '@/presentation/contexts/AuthContext';
import { httpClient } from '@/infrastructure/http/client/httpClient';

type SettingsSection = 'app' | 'whatsapp';
type SettingsTab = 'whatsapp' | 'templatesBrowse' | 'templatesBindings';

type PanelConfig = {
  telefonoContacto1: string | null;
  telefonoContacto2: string | null;
  contacto1: string | null;
  contacto2: string | null;
  updatedAt: string | null;
};

type PlatformCommSettings = {
  mobileAppEnabled: boolean;
  whatsappEnabled: boolean;
  agentEnabled: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
};

type AgentUsageTenantRow = {
  tenantId: string;
  tenantName: string | null;
  costUsd: number;
  tokens: number;
  calls: number;
};

type AgentUsageReport = {
  period: string;
  totals: { costUsd: number; tokens: number; calls: number; tenantCount: number };
  monthlySeries: { period: string; costUsd: number; tokens: number; calls: number }[];
  tenants: AgentUsageTenantRow[];
};

type TemplateBindingRow = {
  eventKey: string;
  product: 'panel' | 'coofit';
  label: string;
  description: string;
  defaultTemplateName: string;
  defaultLanguage: string;
  requiredParams: string[];
  metaTemplateName: string;
  metaLanguage: string;
  updatedAt: string | null;
  updatedBy: string | null;
  source: 'db' | 'default';
};

type MetaTemplateButton = {
  type: string;
  text: string;
  url?: string | null;
};

type MetaTemplate = {
  id: string;
  name: string;
  language: string;
  status: string;
  category: string | null;
  headerText?: string | null;
  bodyText: string | null;
  footerText?: string | null;
  buttons?: MetaTemplateButton[];
};

type TemplateDraft = Record<string, { metaTemplateName: string; metaLanguage: string }>;

/** Datos de ejemplo para mockup del mensaje (sin variables a la vista). */
const MOCK_TEMPLATE_DATA: Record<string, string> = {
  nombre_paciente: 'María González',
  patient_name: 'María González',
  fecha_turno: '15/04/2026',
  appointment_date: '15/04/2026',
  hora_turno: '10:30',
  appointment_time: '10:30',
  medico: 'Dr. Manuel Bravo',
  doctor: 'Dr. Manuel Bravo',
  nombre_clinica: 'Clínica Coofit Centro',
  clinic_name: 'Clínica Coofit Centro',
  direccion_clinica: 'Av. Corrientes 1234, CABA',
  clinic_address: 'Av. Corrientes 1234, CABA',
  info_contacto: 'WhatsApp: +54 9 11 6123-4567 · Tel: (011) 4567-8910',
  contact_info: 'WhatsApp: +54 9 11 6123-4567 · Tel: (011) 4567-8910',
  nombre_cliente: 'Clínica Norte SRL',
  concepto: 'suscripción Coofit Base (Clínica Norte) Abril 2026',
  monto: 'ARS 45.000',
  link_pago: 'https://mpago.la/ejemplo-pago',
  contacto1: 'Lucas',
  contacto2: 'Soporte Coofit',
  telefono_contacto1: '+54 9 11 5555-1111',
  telefono_contacto2: '+54 9 11 5555-2222',
  telefono_contacto_1: '+54 9 11 5555-1111',
  telefono_contacto_2: '+54 9 11 5555-2222',
  // positional fallbacks Meta {{1}} {{2}} …
  '1': 'María González',
  '2': '15/04/2026',
  '3': '10:30',
  '4': 'Dr. Manuel Bravo',
  '5': 'Clínica Coofit Centro',
  '6': 'Av. Corrientes 1234',
  '7': 'WhatsApp +54 9 11 6123-4567',
  '8': 'https://mpago.la/ejemplo-pago',
};

function applyMockDataToBody(body: string | null | undefined): string | null {
  if (!body?.trim()) return null;
  return body.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_full, rawKey: string) => {
    const key = String(rawKey).trim();
    const lower = key.toLowerCase();
    return (
      MOCK_TEMPLATE_DATA[key] ||
      MOCK_TEMPLATE_DATA[lower] ||
      `[${key}]`
    );
  });
}

function mockTimeLabel(): string {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/**
 * Captura estilo WhatsApp (mensaje entrante de negocio):
 * header chat + burbuja blanca con header/body/footer + botones quick-reply debajo.
 */
function WhatsAppChatCapture({
  headerText,
  bodyText,
  footerText,
  emptyLabel,
  mockCaption,
  chatName,
  buttons,
}: {
  headerText?: string | null;
  bodyText: string | null | undefined;
  footerText?: string | null;
  emptyLabel: string;
  mockCaption?: string;
  chatName: string;
  buttons?: MetaTemplateButton[] | null;
}) {
  const displayHeader = applyMockDataToBody(headerText);
  const displayBody = applyMockDataToBody(bodyText);
  const displayFooter = applyMockDataToBody(footerText);
  const actionButtons = (buttons || []).filter((b) => b.text?.trim());
  const time = useMemo(() => mockTimeLabel(), []);
  const hasContent = Boolean(displayHeader || displayBody || displayFooter);

  return (
    <div className="space-y-2">
      {/* Frame teléfono / chat */}
      <div className="w-full max-w-[22rem] overflow-hidden rounded-2xl border border-slate-300 shadow-lg dark:border-slate-600">
        {/* Barra de chat */}
        <div className="flex items-center gap-2.5 bg-[#075e54] px-3 py-2.5 text-white dark:bg-[#1f2c34]">
          <span className="text-lg leading-none opacity-90" aria-hidden>
            ‹
          </span>
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#128c7e] text-sm font-semibold">
            {(chatName.trim()[0] || 'C').toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold leading-tight">{chatName}</p>
            <p className="truncate text-[10px] leading-tight text-white/75">Empresa</p>
          </div>
        </div>

        {/* Fondo chat WhatsApp */}
        <div
          className="relative min-h-[280px] px-2.5 py-3"
          style={{
            backgroundColor: '#e5ddd5',
            backgroundImage:
              'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23c8beb5\' fill-opacity=\'0.22\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          }}
        >
          <div className="mb-2 flex justify-center">
            <span className="rounded-md bg-white/90 px-2 py-0.5 text-[10px] font-medium text-slate-600 shadow-sm">
              Hoy
            </span>
          </div>

          {/* Mensaje entrante (negocio → paciente) */}
          <div className="max-w-[92%]">
            <div className="overflow-hidden rounded-lg rounded-tl-none bg-white shadow-[0_1px_0.5px_rgba(0,0,0,0.13)]">
              <div className="px-2.5 pb-1 pt-1.5">
                {displayHeader ? (
                  <p className="mb-1 text-[14px] font-semibold leading-snug text-[#111b21]">
                    {displayHeader}
                  </p>
                ) : null}
                {displayBody ? (
                  <p className="whitespace-pre-wrap break-words text-[13.5px] leading-[1.35] text-[#111b21]">
                    {displayBody}
                  </p>
                ) : !hasContent ? (
                  <p className="text-xs italic text-slate-500">{emptyLabel}</p>
                ) : null}
                {displayFooter ? (
                  <p className="mt-1.5 text-[12px] leading-snug text-[#667781]">{displayFooter}</p>
                ) : null}
                <div className="mt-0.5 flex items-center justify-end gap-1">
                  <span className="text-[10px] text-[#667781]">{time}</span>
                </div>
              </div>

              {/* Botones interactivos pegados al mensaje (como en WA real) */}
              {actionButtons.length > 0 ? (
                <div className="border-t border-[#e9edef]">
                  {actionButtons.map((btn, i) => {
                    const type = String(btn.type || 'QUICK_REPLY').toUpperCase();
                    const isUrl = type === 'URL';
                    const isPhone = type === 'PHONE_NUMBER';
                    return (
                      <div
                        key={`${btn.type}-${btn.text}-${i}`}
                        className={`flex items-center justify-center gap-1.5 px-3 py-2.5 text-center ${
                          i > 0 ? 'border-t border-[#e9edef]' : ''
                        }`}
                      >
                        {isUrl || isPhone ? (
                          <svg
                            className="h-3.5 w-3.5 shrink-0 text-[#027eb5]"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            aria-hidden
                          >
                            {isPhone ? (
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                              />
                            ) : (
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                              />
                            )}
                          </svg>
                        ) : null}
                        <span
                          className={`text-[14px] font-medium leading-tight ${
                            isUrl || isPhone ? 'text-[#027eb5]' : 'text-[#027eb5]'
                          }`}
                        >
                          {btn.text}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
      {mockCaption ? (
        <p className="max-w-[22rem] text-[11px] text-slate-500 dark:text-slate-400">{mockCaption}</p>
      ) : null}
    </div>
  );
}

function KillSwitchRow({
  title,
  description,
  enabled,
  onLabel,
  offLabel,
  disabled,
  saving,
  onToggle,
}: {
  title: string;
  description: string;
  enabled: boolean;
  onLabel: string;
  offLabel: string;
  disabled: boolean;
  saving: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50/80 p-3 dark:border-gray-700 dark:bg-gray-900/40">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-gray-900 dark:text-white">{title}</p>
        <p className="mt-0.5 text-[11px] leading-snug text-gray-500 dark:text-gray-400">{description}</p>
        <p
          className={`mt-1.5 text-[11px] font-medium ${
            enabled
              ? 'text-teal-700 dark:text-teal-300'
              : 'text-rose-600 dark:text-rose-400'
          }`}
        >
          {enabled ? onLabel : offLabel}
        </p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        disabled={disabled || saving}
        onClick={onToggle}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50 dark:focus:ring-offset-gray-800 ${
          enabled ? 'bg-teal-600' : 'bg-slate-300 dark:bg-gray-600'
        }`}
      >
        <span
          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ${
            enabled ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </button>
    </div>
  );
}

function formatUsd(value: number): string {
  const n = Number.isFinite(value) ? value : 0;
  // Con gastos chicos (centavos/día) conviene mostrar hasta 2 decimales.
  return `US$ ${n.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatCompact(value: number): string {
  const n = Number.isFinite(value) ? value : 0;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function templateOptionKey(name: string, language: string): string {
  return `${name}::${language}`;
}

function parseTemplateOptionKey(value: string): { name: string; language: string } | null {
  const idx = value.indexOf('::');
  if (idx <= 0) return null;
  return { name: value.slice(0, idx), language: value.slice(idx + 2) };
}

function findMetaTemplate(
  list: MetaTemplate[],
  name: string,
  language: string
): MetaTemplate | undefined {
  return (
    list.find((m) => m.name === name && m.language === language) ||
    list.find((m) => m.name === name)
  );
}

type BindingCaseLang = 'es' | 'pt' | 'en';
type BindingCaseType =
  | 'panel_payment'
  | 'panel_suspension'
  | 'shared_basic'
  | 'dedicated_basic'
  | 'reminder_contact';

function normalizeBindingLang(language: string | null | undefined): BindingCaseLang | null {
  const raw = String(language || '').trim().toLowerCase();
  if (!raw) return null;
  if (raw === 'es' || raw.startsWith('es_') || raw.startsWith('es-')) return 'es';
  if (raw === 'pt' || raw.startsWith('pt_') || raw.startsWith('pt-')) return 'pt';
  if (raw === 'en' || raw.startsWith('en_') || raw.startsWith('en-')) return 'en';
  // event_key suffix …es|pt|en
  if (raw.endsWith('.es') || raw === 'es') return 'es';
  if (raw.endsWith('.pt') || raw === 'pt') return 'pt';
  if (raw.endsWith('.en') || raw === 'en') return 'en';
  return null;
}

function bindingCaseLang(row: {
  eventKey: string;
  defaultLanguage: string;
  metaLanguage: string;
}): BindingCaseLang | null {
  return (
    normalizeBindingLang(row.defaultLanguage) ||
    normalizeBindingLang(row.metaLanguage) ||
    normalizeBindingLang(row.eventKey.split('.').pop())
  );
}

function bindingCaseType(eventKey: string): BindingCaseType | null {
  const k = String(eventKey || '').toLowerCase();
  if (k.includes('payment_reminder')) return 'panel_payment';
  if (k.includes('service_suspension')) return 'panel_suspension';
  if (k.includes('.contact.') || k.includes('_contact') || k.includes('.contact')) return 'reminder_contact';
  if (k.includes('shared.basic') || k.includes('shared_basic')) return 'shared_basic';
  if (k.includes('dedicated.basic') || k.includes('dedicated_basic')) return 'dedicated_basic';
  return null;
}

function bindingTypeGroupLabel(
  type: BindingCaseType | 'other' | null,
  t: (key: string) => string
): string {
  switch (type) {
    case 'panel_payment':
      return t('pages.settings.templatesTypePayment');
    case 'panel_suspension':
      return t('pages.settings.templatesTypeSuspension');
    case 'shared_basic':
      return t('pages.settings.templatesTypeShared');
    case 'dedicated_basic':
      return t('pages.settings.templatesTypeDedicated');
    case 'reminder_contact':
      return t('pages.settings.templatesTypeContact');
    default:
      return t('pages.settings.templatesGroupOther');
  }
}

function StatusPill({ status }: { status: string }) {
  const upper = String(status || '').toUpperCase();
  const styles =
    upper === 'APPROVED'
      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
      : upper === 'PENDING' || upper === 'IN_APPEAL'
        ? 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200'
        : 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200';
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${styles}`}
    >
      {upper || '—'}
    </span>
  );
}

export const SettingsPage: React.FC<{ section?: SettingsSection }> = ({ section: sectionProp }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const section: SettingsSection =
    sectionProp ??
    (location.pathname.includes('configuracion-app') ? 'app' : 'whatsapp');
  const { canAdminister } = useAuth();
  const { success, error: showError } = useToast();
  const [activeTab, setActiveTab] = useState<SettingsTab>('whatsapp');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingKill, setIsSavingKill] = useState(false);
  const [phone1, setPhone1] = useState('');
  const [phone2, setPhone2] = useState('');
  const [contacto1, setContacto1] = useState('');
  const [contacto2, setContacto2] = useState('');
  const [platform, setPlatform] = useState<PlatformCommSettings | null>(null);
  const [agentUsage, setAgentUsage] = useState<AgentUsageReport | null>(null);
  const [agentUsageLoading, setAgentUsageLoading] = useState(false);

  const [bindings, setBindings] = useState<TemplateBindingRow[]>([]);
  const [draft, setDraft] = useState<TemplateDraft>({});
  const [metaTemplates, setMetaTemplates] = useState<MetaTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState<string | null>(null);
  const [isSavingTemplates, setIsSavingTemplates] = useState(false);
  const [templatesLoaded, setTemplatesLoaded] = useState(false);
  const [browseKey, setBrowseKey] = useState<string>('');
  const [selectedBindingSection, setSelectedBindingSection] = useState<
    BindingCaseType | 'other' | null
  >(null);
  const [bindingsLangFilter, setBindingsLangFilter] = useState<'all' | BindingCaseLang>('all');

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const [panelRes, platformRes] = await Promise.all([
        httpClient.get<PanelConfig>('/panel-config'),
        httpClient.get<PlatformCommSettings>('/platform-communication-settings'),
      ]);
      setPhone1(panelRes.data.telefonoContacto1 || '');
      setPhone2(panelRes.data.telefonoContacto2 || '');
      setContacto1(panelRes.data.contacto1 || '');
      setContacto2(panelRes.data.contacto2 || '');
      setPlatform(platformRes.data);
    } catch (err) {
      showError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setIsLoading(false);
    }
  }, [showError, t]);

  const applyBindings = useCallback((items: TemplateBindingRow[]) => {
    setBindings(items);
    const next: TemplateDraft = {};
    for (const row of items) {
      next[row.eventKey] = {
        metaTemplateName: row.metaTemplateName,
        metaLanguage: row.metaLanguage,
      };
    }
    setDraft(next);
  }, []);

  const loadTemplates = useCallback(async (forceRefresh = false) => {
    setTemplatesLoading(true);
    setTemplatesError(null);
    try {
      const metaPath = forceRefresh
        ? '/whatsapp-templates/meta?refresh=1'
        : '/whatsapp-templates/meta';
      const [bindingsRes, metaRes] = await Promise.all([
        httpClient.get<{ items: TemplateBindingRow[] }>('/whatsapp-templates/bindings'),
        httpClient.get<{ items: MetaTemplate[] }>(metaPath).catch((err: unknown) => {
          const axiosMsg =
            err && typeof err === 'object' && 'response' in err
              ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
              : undefined;
          throw new Error(axiosMsg || (err instanceof Error ? err.message : t('common.error')));
        }),
      ]);
      applyBindings(bindingsRes.data.items || []);
      const items = metaRes.data.items || [];
      setMetaTemplates(items);
      setTemplatesLoaded(true);
      setBrowseKey((prev) => {
        if (prev && items.some((m) => templateOptionKey(m.name, m.language) === prev)) {
          return prev;
        }
        // Preferir plantilla con botones (confirm/reject) para que se note el mockup real
        const withButtons = items.find(
          (m) =>
            String(m.status).toUpperCase() === 'APPROVED' &&
            Array.isArray(m.buttons) &&
            m.buttons.length >= 2
        );
        const firstApproved =
          withButtons ||
          items.find((m) => String(m.status).toUpperCase() === 'APPROVED') ||
          items[0];
        return firstApproved
          ? templateOptionKey(firstApproved.name, firstApproved.language)
          : '';
      });
    } catch (err) {
      try {
        const bindingsRes = await httpClient.get<{ items: TemplateBindingRow[] }>(
          '/whatsapp-templates/bindings'
        );
        applyBindings(bindingsRes.data.items || []);
        setTemplatesLoaded(true);
      } catch {
        /* ignore */
      }
      const msg = err instanceof Error ? err.message : t('common.error');
      setTemplatesError(msg);
      showError(msg);
    } finally {
      setTemplatesLoading(false);
    }
  }, [applyBindings, showError, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadAgentUsage = useCallback(async () => {
    setAgentUsageLoading(true);
    try {
      const res = await httpClient.get<AgentUsageReport>('/agent-usage');
      setAgentUsage(res.data);
    } catch (err) {
      // No es crítico: el consumo es informativo. Log silencioso.
      console.warn('No se pudo cargar el consumo de IA', err);
    } finally {
      setAgentUsageLoading(false);
    }
  }, []);

  useEffect(() => {
    if (section === 'app') void loadAgentUsage();
  }, [section, loadAgentUsage]);

  useEffect(() => {
    if (section !== 'whatsapp') return;
    const needsTemplates =
      activeTab === 'templatesBrowse' || activeTab === 'templatesBindings';
    if (needsTemplates && !templatesLoaded && !templatesLoading) {
      void loadTemplates(true);
    }
  }, [section, activeTab, templatesLoaded, templatesLoading, loadTemplates]);

  const handleSaveContacts = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canAdminister) return;
    setIsSaving(true);
    try {
      const res = await httpClient.put<PanelConfig>('/panel-config', {
        telefonoContacto1: phone1.trim() || null,
        telefonoContacto2: phone2.trim() || null,
        contacto1: contacto1.trim() || null,
        contacto2: contacto2.trim() || null,
      });
      setPhone1(res.data.telefonoContacto1 || '');
      setPhone2(res.data.telefonoContacto2 || '');
      setContacto1(res.data.contacto1 || '');
      setContacto2(res.data.contacto2 || '');
      success(t('pages.settings.saved'));
    } catch (err) {
      const axiosMsg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      showError(axiosMsg || (err instanceof Error ? err.message : t('common.error')));
    } finally {
      setIsSaving(false);
    }
  };

  const updateKillSwitch = async (
    patch: Partial<Pick<PlatformCommSettings, 'mobileAppEnabled' | 'whatsappEnabled' | 'agentEnabled'>>,
  ) => {
    if (!canAdminister || !platform) return;
    setIsSavingKill(true);
    try {
      const res = await httpClient.put<PlatformCommSettings>(
        '/platform-communication-settings',
        patch,
      );
      setPlatform(res.data);
      success(t('pages.settings.killSwitchesSaved'));
    } catch (err) {
      const axiosMsg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      showError(axiosMsg || (err instanceof Error ? err.message : t('common.error')));
    } finally {
      setIsSavingKill(false);
    }
  };

  const dirty = useMemo(() => {
    if (bindings.length === 0) return false;
    return bindings.some((row) => {
      const d = draft[row.eventKey];
      if (!d) return false;
      return (
        d.metaTemplateName !== row.metaTemplateName || d.metaLanguage !== row.metaLanguage
      );
    });
  }, [bindings, draft]);

  const approvedMeta = useMemo(
    () => metaTemplates.filter((m) => String(m.status).toUpperCase() === 'APPROVED'),
    [metaTemplates]
  );

  const browseTemplate = useMemo(() => {
    const parsed = parseTemplateOptionKey(browseKey);
    if (!parsed) return null;
    return findMetaTemplate(metaTemplates, parsed.name, parsed.language) || null;
  }, [browseKey, metaTemplates]);

  const isApprovedMeta = useCallback(
    (name: string, language: string) =>
      approvedMeta.some((m) => m.name === name && m.language === language),
    [approvedMeta]
  );

  const handleSaveTemplates = async () => {
    if (!canAdminister || !dirty) return;
    setIsSavingTemplates(true);
    try {
      const items = bindings.flatMap((row) => {
        const d = draft[row.eventKey];
        if (!d) return [];
        const changed =
          d.metaTemplateName !== row.metaTemplateName ||
          d.metaLanguage !== row.metaLanguage;
        if (!changed || !isApprovedMeta(d.metaTemplateName, d.metaLanguage)) {
          return [];
        }
        return [
          {
            eventKey: row.eventKey,
            metaTemplateName: d.metaTemplateName,
            metaLanguage: d.metaLanguage,
          },
        ];
      });

      if (items.length === 0) {
        showError(t('pages.settings.templatesSaveNeedMeta'));
        return;
      }

      const res = await httpClient.put<{ items: TemplateBindingRow[] }>(
        '/whatsapp-templates/bindings',
        { items }
      );
      applyBindings(res.data.items || []);
      success(t('pages.settings.templatesSaved'));
    } catch (err) {
      const axiosMsg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      showError(axiosMsg || (err instanceof Error ? err.message : t('common.error')));
    } finally {
      setIsSavingTemplates(false);
    }
  };

  const setDraftFromOption = (eventKey: string, optionValue: string) => {
    if (!optionValue) return;
    const parsed = parseTemplateOptionKey(optionValue);
    if (!parsed) return;
    const exists = approvedMeta.some(
      (m) => m.name === parsed.name && m.language === parsed.language
    );
    if (!exists) return;
    setDraft((prev) => ({
      ...prev,
      [eventKey]: {
        metaTemplateName: parsed.name,
        metaLanguage: parsed.language,
      },
    }));
  };

  const tabs: { key: SettingsTab; label: string }[] = [
    { key: 'whatsapp', label: t('pages.settings.tabWhatsapp') },
    { key: 'templatesBrowse', label: t('pages.settings.templatesInnerBrowse') },
    { key: 'templatesBindings', label: t('pages.settings.templatesInnerBindings') },
  ];

  const isTemplatesTab =
    section === 'whatsapp' &&
    (activeTab === 'templatesBrowse' || activeTab === 'templatesBindings');

  const pageTitle =
    section === 'app'
      ? t('pages.settings.tabCoofitApp')
      : t('menu.whatsappSettings');

  const BINDING_SECTION_ORDER: (BindingCaseType | 'other')[] = [
    'panel_payment',
    'panel_suspension',
    'shared_basic',
    'dedicated_basic',
    'reminder_contact',
    'other',
  ];

  const bindingSections = useMemo(() => {
    const map = new Map<BindingCaseType | 'other', TemplateBindingRow[]>();
    for (const row of bindings) {
      const type: BindingCaseType | 'other' = bindingCaseType(row.eventKey) || 'other';
      const list = map.get(type) || [];
      list.push(row);
      map.set(type, list);
    }
    return BINDING_SECTION_ORDER.filter((type) => (map.get(type)?.length || 0) > 0).map(
      (type) => ({
        type,
        rows: map.get(type) || [],
        label: bindingTypeGroupLabel(type, t),
      })
    );
  }, [bindings, t]);

  /** Fila de la sección activa, opcionalmente filtrada por idioma. */
  const selectedSectionRows = useMemo(() => {
    const sectionRows =
      bindingSections.find((s) => s.type === selectedBindingSection)?.rows || [];
    if (bindingsLangFilter === 'all') return sectionRows;
    return sectionRows.filter((row) => bindingCaseLang(row) === bindingsLangFilter);
  }, [bindingSections, selectedBindingSection, bindingsLangFilter]);

  // Abrir la primera sección cuando cargan los bindings o si la actual desaparece
  useEffect(() => {
    if (bindingSections.length === 0) {
      setSelectedBindingSection(null);
      return;
    }
    if (
      !selectedBindingSection ||
      !bindingSections.some((s) => s.type === selectedBindingSection)
    ) {
      setSelectedBindingSection(bindingSections[0].type);
    }
  }, [bindingSections, selectedBindingSection]);

  const renderBindingRows = (rows: TemplateBindingRow[]) => {
    if (rows.length === 0) {
      return (
        <p className="rounded-lg border border-dashed border-slate-200 px-3 py-8 text-center text-sm text-slate-500 dark:border-gray-700 dark:text-slate-400">
          {t('pages.settings.templatesFilterEmpty')}
        </p>
      );
    }
    return (
      <ul className="space-y-1.5">
        {rows.map((row) => {
          const d = draft[row.eventKey] || {
            metaTemplateName: row.metaTemplateName,
            metaLanguage: row.metaLanguage,
          };
          const inMeta = isApprovedMeta(d.metaTemplateName, d.metaLanguage);
          const selectValue = inMeta
            ? templateOptionKey(d.metaTemplateName, d.metaLanguage)
            : '';
          const isDirtyRow =
            d.metaTemplateName !== row.metaTemplateName ||
            d.metaLanguage !== row.metaLanguage;

          return (
            <li
              key={row.eventKey}
              className="rounded-lg border border-slate-200 bg-white p-2.5 dark:border-gray-700 dark:bg-gray-800/80"
            >
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {row.label}
                    </p>
                    {isDirtyRow ? (
                      <span className="h-1.5 w-1.5 rounded-full bg-amber-500" title="Sin guardar" />
                    ) : null}
                    {inMeta ? (
                      <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[9px] font-medium text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">
                        OK
                      </span>
                    ) : (
                      <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-medium text-amber-900 dark:bg-amber-900/40 dark:text-amber-200">
                        {t('pages.settings.templatesStatusWarn')}
                      </span>
                    )}
                  </div>
                  <p className="mt-0.5 line-clamp-2 text-[11px] text-gray-500 dark:text-gray-400">
                    {row.description}
                  </p>
                  {!inMeta && d.metaTemplateName ? (
                    <p className="mt-1 text-[11px] text-amber-700 dark:text-amber-300">
                      {t('pages.settings.templateNotInMeta')}{' '}
                      <span className="font-mono">
                        {d.metaTemplateName} · {d.metaLanguage}
                      </span>
                    </p>
                  ) : null}
                </div>
                <div className="w-full shrink-0 sm:w-72 lg:w-80">
                  <select
                    className="w-full max-w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-gray-900 shadow-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                    value={selectValue}
                    disabled={!canAdminister || templatesLoading || approvedMeta.length === 0}
                    onChange={(e) => setDraftFromOption(row.eventKey, e.target.value)}
                    aria-label={t('pages.settings.templateSelect')}
                  >
                    <option value="">
                      {t('pages.settings.templatesChoosePlaceholder')}
                    </option>
                    {approvedMeta.map((m) => (
                      <option
                        key={`${row.eventKey}-${m.name}-${m.language}`}
                        value={templateOptionKey(m.name, m.language)}
                      >
                        {m.name} · {m.language}
                        {m.buttons?.some((b) => b.type === 'URL') ? ' · URL' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <div className="panel-page h-full min-w-0 flex flex-col overflow-hidden !space-y-2 !p-3 sm:!p-3.5 md:!p-4">
      <div className="flex-shrink-0 space-y-1.5">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <h1 className="text-lg font-bold tracking-tight text-slate-900 dark:text-white md:text-xl">
              {pageTitle}
            </h1>
            <p className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400 sm:text-xs">
              {section === 'app'
                ? t('pages.settings.tabCoofitAppSubtitle')
                : t('pages.settings.tabWhatsappSubtitle')}
            </p>
          </div>
          {isTemplatesTab ? (
            <div className="flex shrink-0 items-center gap-1.5">
              {dirty && activeTab === 'templatesBindings' ? (
                <span
                  className="hidden h-1.5 w-1.5 rounded-full bg-amber-500 sm:inline-block"
                  title={t('pages.settings.templatesDirtyHint')}
                />
              ) : null}
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={templatesLoading}
                onClick={() => {
                  void loadTemplates(true);
                }}
              >
                {templatesLoading ? t('common.loading') : t('pages.settings.templatesRefresh')}
              </Button>
              {activeTab === 'templatesBindings' && canAdminister ? (
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  disabled={!dirty || isSavingTemplates || templatesLoading}
                  onClick={() => void handleSaveTemplates()}
                >
                  {isSavingTemplates ? t('common.saving') : t('common.save')}
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
        {section === 'whatsapp' ? (
          <nav
            className="flex gap-0.5 overflow-x-auto border-b border-slate-200 dark:border-gray-700"
            aria-label={pageTitle}
          >
            {tabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`whitespace-nowrap border-b-2 px-2.5 py-1.5 text-xs font-medium transition-colors sm:text-sm ${
                  activeTab === tab.key
                    ? 'border-teal-500 text-teal-600 dark:text-teal-400'
                    : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        ) : null}
      </div>

      <div className="min-h-0 flex-1 overflow-hidden flex flex-col">
        {section === 'app' ? (
          <div className="min-h-0 flex-1 overflow-y-auto pb-4">
            <section className="max-w-xl space-y-3 rounded-xl border border-slate-200 bg-white p-3.5 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-4">
              {isLoading || !platform ? (
                <p className="text-sm text-gray-500">{t('common.loading')}</p>
              ) : (
                <>
                  <KillSwitchRow
                    title={t('pages.settings.mobileAppSwitch')}
                    description={t('pages.settings.mobileAppSwitchHint')}
                    enabled={platform.mobileAppEnabled}
                    onLabel={t('pages.settings.switchOn')}
                    offLabel={t('pages.settings.switchOff')}
                    disabled={!canAdminister}
                    saving={isSavingKill}
                    onToggle={() =>
                      void updateKillSwitch({ mobileAppEnabled: !platform.mobileAppEnabled })
                    }
                  />
                  <KillSwitchRow
                    title={t('pages.settings.agentSwitch')}
                    description={t('pages.settings.agentSwitchHint')}
                    enabled={platform.agentEnabled}
                    onLabel={t('pages.settings.agentSwitchOn')}
                    offLabel={t('pages.settings.agentSwitchOff')}
                    disabled={!canAdminister}
                    saving={isSavingKill}
                    onToggle={() =>
                      void updateKillSwitch({ agentEnabled: !platform.agentEnabled })
                    }
                  />
                  {platform.updatedAt ? (
                    <p className="text-[11px] text-slate-500 dark:text-slate-400">
                      {t('pages.settings.killSwitchesUpdated', {
                        at: new Date(platform.updatedAt).toLocaleString(),
                        by: platform.updatedBy || '—',
                      })}
                    </p>
                  ) : null}
                </>
              )}
              {!canAdminister ? (
                <p className="text-xs text-gray-500">{t('pages.settings.readOnly')}</p>
              ) : null}
            </section>

            <section className="mt-3 max-w-xl space-y-3 rounded-xl border border-slate-200 bg-white p-3.5 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-4">
              <div className="flex items-baseline justify-between gap-2">
                <div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    {t('pages.settings.agentUsageTitle')}
                  </p>
                  <p className="mt-0.5 text-[11px] leading-snug text-gray-500 dark:text-gray-400">
                    {t('pages.settings.agentUsageHint')}
                  </p>
                </div>
                {agentUsage ? (
                  <span className="whitespace-nowrap text-[11px] font-medium text-slate-500 dark:text-slate-400">
                    {agentUsage.period}
                  </span>
                ) : null}
              </div>

              {agentUsageLoading && !agentUsage ? (
                <p className="text-sm text-gray-500">{t('common.loading')}</p>
              ) : !agentUsage ? (
                <p className="text-sm text-gray-500">{t('pages.settings.agentUsageEmpty')}</p>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="rounded-lg border border-slate-200 bg-slate-50/80 p-2.5 dark:border-gray-700 dark:bg-gray-900/40">
                      <p className="text-[11px] text-gray-500 dark:text-gray-400">
                        {t('pages.settings.agentUsageCost')}
                      </p>
                      <p className="mt-0.5 text-lg font-bold text-teal-700 dark:text-teal-300">
                        {formatUsd(agentUsage.totals.costUsd)}
                      </p>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-slate-50/80 p-2.5 dark:border-gray-700 dark:bg-gray-900/40">
                      <p className="text-[11px] text-gray-500 dark:text-gray-400">
                        {t('pages.settings.agentUsageCalls')}
                      </p>
                      <p className="mt-0.5 text-lg font-bold text-gray-900 dark:text-white">
                        {agentUsage.totals.calls.toLocaleString()}
                      </p>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-slate-50/80 p-2.5 dark:border-gray-700 dark:bg-gray-900/40">
                      <p className="text-[11px] text-gray-500 dark:text-gray-400">
                        {t('pages.settings.agentUsageTokens')}
                      </p>
                      <p className="mt-0.5 text-lg font-bold text-gray-900 dark:text-white">
                        {formatCompact(agentUsage.totals.tokens)}
                      </p>
                    </div>
                  </div>

                  {agentUsage.tenants.length > 0 ? (
                    <div className="space-y-1">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        {t('pages.settings.agentUsageTopClinics')}
                      </p>
                      <div className="divide-y divide-slate-100 dark:divide-gray-700">
                        {agentUsage.tenants.slice(0, 5).map((row) => (
                          <div
                            key={row.tenantId}
                            className="flex items-center justify-between gap-3 py-1.5"
                          >
                            <span className="min-w-0 flex-1 truncate text-xs text-gray-700 dark:text-gray-200">
                              {row.tenantName || row.tenantId}
                            </span>
                            <span className="whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                              {row.calls.toLocaleString()} · {formatUsd(row.costUsd)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500">{t('pages.settings.agentUsageNoData')}</p>
                  )}
                </>
              )}
            </section>
          </div>
        ) : !isTemplatesTab ? (
          <div className="min-h-0 flex-1 overflow-y-auto pb-4">
            {activeTab === 'whatsapp' ? (
              <div className="max-w-xl space-y-3">
                <section className="space-y-3 rounded-xl border border-slate-200 bg-white p-3.5 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-4">
                  {isLoading || !platform ? (
                    <p className="text-sm text-gray-500">{t('common.loading')}</p>
                  ) : (
                    <>
                      <KillSwitchRow
                        title={t('pages.settings.whatsappPlatformSwitch')}
                        description={t('pages.settings.whatsappPlatformSwitchHint')}
                        enabled={platform.whatsappEnabled}
                        onLabel={t('pages.settings.switchOn')}
                        offLabel={t('pages.settings.switchOff')}
                        disabled={!canAdminister}
                        saving={isSavingKill}
                        onToggle={() =>
                          void updateKillSwitch({ whatsappEnabled: !platform.whatsappEnabled })
                        }
                      />
                      {platform.updatedAt ? (
                        <p className="text-[11px] text-slate-500 dark:text-slate-400">
                          {t('pages.settings.killSwitchesUpdated', {
                            at: new Date(platform.updatedAt).toLocaleString(),
                            by: platform.updatedBy || '—',
                          })}
                        </p>
                      ) : null}
                    </>
                  )}
                </section>
                <form
                  onSubmit={(e) => void handleSaveContacts(e)}
                  className="space-y-3 rounded-xl border border-slate-200 bg-white p-3.5 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-4"
                >
                  <p className="text-xs font-semibold text-gray-900 dark:text-white">
                    {t('pages.settings.contactPhones')}
                  </p>
                  {isLoading ? (
                    <p className="text-sm text-gray-500">{t('common.loading')}</p>
                  ) : (
                    <>
                      <Input
                        label={t('pages.settings.contacto1')}
                        type="text"
                        value={contacto1}
                        onChange={(e) => setContacto1(e.target.value)}
                        placeholder={t('pages.settings.contacto1Placeholder')}
                        disabled={!canAdminister}
                      />
                      <Input
                        label={t('pages.settings.phone1')}
                        type="tel"
                        value={phone1}
                        onChange={(e) => setPhone1(e.target.value)}
                        placeholder="+54 9 11 1234-5678"
                        disabled={!canAdminister}
                      />
                      <Input
                        label={t('pages.settings.contacto2')}
                        type="text"
                        value={contacto2}
                        onChange={(e) => setContacto2(e.target.value)}
                        placeholder={t('pages.settings.contacto2Placeholder')}
                        disabled={!canAdminister}
                      />
                      <Input
                        label={t('pages.settings.phone2')}
                        type="tel"
                        value={phone2}
                        onChange={(e) => setPhone2(e.target.value)}
                        placeholder="+54 9 11 8765-4321"
                        disabled={!canAdminister}
                      />
                    </>
                  )}
                  {canAdminister ? (
                    <div className="pt-0.5">
                      <Button type="submit" variant="primary" size="sm" disabled={isLoading || isSaving}>
                        {isSaving ? t('common.saving') : t('common.save')}
                      </Button>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500">{t('pages.settings.readOnly')}</p>
                  )}
                </form>
              </div>
            ) : null}
          </div>
        ) : (
          <div
            className={`flex h-full min-h-0 w-full flex-col overflow-hidden ${
              activeTab === 'templatesBindings' || activeTab === 'templatesBrowse'
                ? 'max-w-none'
                : 'mx-auto max-w-3xl'
            }`}
          >
            {templatesError ? (
              <p className="mb-2 flex-shrink-0 rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-200">
                {templatesError}
              </p>
            ) : null}

            <div className="flex min-h-0 flex-1 flex-col overflow-hidden pb-3">
              {templatesLoading && metaTemplates.length === 0 && bindings.length === 0 ? (
                <p className="text-sm text-gray-500">{t('common.loading')}</p>
              ) : null}

              {activeTab === 'templatesBrowse' ? (
                <section className="flex h-full min-h-0 flex-col gap-3 lg:flex-row lg:gap-4">
                  <nav
                    className="flex max-h-[42vh] min-h-0 shrink-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-gray-700 dark:bg-gray-800 lg:max-h-none lg:h-full lg:w-[22rem] xl:w-[26rem]"
                    aria-label={t('pages.settings.templatesInnerBrowse')}
                  >
                    <div className="flex shrink-0 items-center justify-between gap-2 border-b border-slate-100 px-3 py-2 dark:border-gray-700">
                      <p className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                        {t('pages.settings.templatesInnerBrowse')}
                      </p>
                      {metaTemplates.length > 0 ? (
                        <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] tabular-nums text-slate-500 dark:bg-gray-900 dark:text-slate-400">
                          {metaTemplates.length}
                        </span>
                      ) : null}
                    </div>
                    <div className="min-h-0 flex-1 overflow-y-auto p-1.5">
                      {templatesLoading && metaTemplates.length === 0 ? (
                        <p className="px-2 py-3 text-xs text-slate-500">{t('common.loading')}</p>
                      ) : metaTemplates.length === 0 ? (
                        <p className="px-2 py-3 text-xs text-slate-500">
                          {t('pages.settings.metaTemplatesEmpty')}
                        </p>
                      ) : (
                        <ul className="space-y-0.5">
                          {metaTemplates.map((m) => {
                            const key = templateOptionKey(m.name, m.language);
                            const active = browseKey === key;
                            const approved = String(m.status).toUpperCase() === 'APPROVED';
                            return (
                              <li key={`${m.id}-${key}`}>
                                <button
                                  type="button"
                                  onClick={() => setBrowseKey(key)}
                                  className={`flex w-full flex-col gap-1 rounded-lg px-2.5 py-2 text-left transition-colors ${
                                    active
                                      ? 'bg-teal-50 text-teal-900 ring-1 ring-inset ring-teal-200 dark:bg-teal-950/50 dark:text-teal-100 dark:ring-teal-800'
                                      : 'text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-gray-900/70'
                                  }`}
                                >
                                  <span className="break-all text-[13px] font-medium leading-snug">
                                    {m.name}
                                  </span>
                                  <span className="flex flex-wrap items-center gap-1 text-[11px] text-slate-500 dark:text-slate-400">
                                    <span className="uppercase tracking-wide">{m.language}</span>
                                    {!approved ? (
                                      <span className="rounded bg-amber-100 px-1 py-px font-semibold uppercase text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                                        {m.status}
                                      </span>
                                    ) : (
                                      <span className="rounded bg-emerald-100 px-1 py-px font-medium text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">
                                        {m.status}
                                      </span>
                                    )}
                                    {(m.buttons?.length || 0) > 0 ? (
                                      <span className="rounded bg-sky-100 px-1 py-px font-medium text-sky-800 dark:bg-sky-900/40 dark:text-sky-200">
                                        {t('pages.settings.templatesHasButtons', {
                                          count: m.buttons!.length,
                                        })}
                                      </span>
                                    ) : null}
                                  </span>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  </nav>

                  <div className="min-h-0 min-w-0 flex-1 overflow-y-auto">
                    {browseTemplate ? (
                      <div className="space-y-2">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="break-all rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs dark:bg-gray-900">
                            {browseTemplate.name}
                          </span>
                          <span className="text-xs uppercase text-slate-500">
                            {browseTemplate.language}
                          </span>
                          <StatusPill status={browseTemplate.status} />
                          {(browseTemplate.buttons?.length || 0) > 0 ? (
                            <span className="rounded-full bg-sky-100 px-1.5 py-0.5 text-[10px] font-medium text-sky-800 dark:bg-sky-900/40 dark:text-sky-200">
                              {t('pages.settings.templatesHasButtons', {
                                count: browseTemplate.buttons!.length,
                              })}
                            </span>
                          ) : null}
                        </div>
                        <WhatsAppChatCapture
                          chatName={t('pages.settings.templatesChatName')}
                          headerText={browseTemplate.headerText}
                          bodyText={browseTemplate.bodyText}
                          footerText={browseTemplate.footerText}
                          buttons={browseTemplate.buttons}
                          emptyLabel={t('pages.settings.templatesNoBody')}
                          mockCaption={t('pages.settings.templatesMockCaption')}
                        />
                      </div>
                    ) : (
                      <WhatsAppChatCapture
                        chatName={t('pages.settings.templatesChatName')}
                        bodyText={null}
                        emptyLabel={t('pages.settings.templatesPreviewEmpty')}
                      />
                    )}
                  </div>
                </section>
              ) : (
                <section className="flex h-full min-h-0 flex-col gap-3 lg:flex-row lg:gap-4">
                  {/* Lista de secciones (izquierda) */}
                  <nav
                    className="flex max-h-[36vh] min-h-0 shrink-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-gray-700 dark:bg-gray-800 lg:max-h-none lg:h-full lg:w-64 xl:w-72"
                    aria-label={t('pages.settings.templatesInnerBindings')}
                  >
                    <div className="min-h-0 flex-1 overflow-y-auto p-1.5">
                    {bindings.length === 0 && templatesLoading ? (
                      <p className="px-2 py-3 text-xs text-slate-500">{t('common.loading')}</p>
                    ) : (
                      bindingSections.map((sec) => {
                        const active = selectedBindingSection === sec.type;
                        const count =
                          bindingsLangFilter === 'all'
                            ? sec.rows.length
                            : sec.rows.filter((r) => bindingCaseLang(r) === bindingsLangFilter)
                                .length;
                        return (
                          <button
                            key={sec.type}
                            type="button"
                            onClick={() => setSelectedBindingSection(sec.type)}
                            className={`mb-0.5 flex w-full items-center justify-between gap-2 rounded-lg px-2.5 py-2 text-left text-sm font-medium transition-colors ${
                              active
                                ? 'bg-teal-50 text-teal-900 ring-1 ring-inset ring-teal-200 dark:bg-teal-950/50 dark:text-teal-100 dark:ring-teal-800'
                                : 'text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-gray-900/70'
                            }`}
                          >
                            <span className="leading-snug">{sec.label}</span>
                            <span
                              className={`shrink-0 rounded-full px-1.5 py-0.5 text-[11px] tabular-nums ${
                                active
                                  ? 'bg-teal-100 text-teal-800 dark:bg-teal-900/60 dark:text-teal-100'
                                  : 'bg-slate-100 text-slate-500 dark:bg-gray-900 dark:text-slate-400'
                              }`}
                            >
                              {count}
                            </span>
                          </button>
                        );
                      })
                    )}
                    </div>
                  </nav>

                  {/* Detalle de la sección (derecha) */}
                  <div className="min-h-0 min-w-0 flex-1 flex flex-col gap-2">
                    <div className="flex flex-shrink-0 flex-wrap items-center justify-between gap-2">
                      <h3 className="min-w-0 text-sm font-semibold text-slate-900 dark:text-white">
                        {bindingSections.find((s) => s.type === selectedBindingSection)?.label ||
                          t('pages.settings.templatesInnerBindings')}
                      </h3>
                      <div className="flex items-center gap-2">
                        <label
                          htmlFor="bindings-lang-filter"
                          className="text-[10px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400"
                        >
                          {t('pages.settings.templatesFilterLang')}
                        </label>
                        <select
                          id="bindings-lang-filter"
                          className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm text-gray-900 shadow-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                          value={bindingsLangFilter}
                          onChange={(e) =>
                            setBindingsLangFilter(e.target.value as 'all' | BindingCaseLang)
                          }
                        >
                          <option value="all">{t('pages.settings.templatesFilterAll')}</option>
                          <option value="es">{t('pages.settings.templatesLangEs')}</option>
                          <option value="pt">{t('pages.settings.templatesLangPt')}</option>
                          <option value="en">{t('pages.settings.templatesLangEn')}</option>
                        </select>
                      </div>
                    </div>

                    <div className="min-h-0 flex-1 overflow-y-auto pb-1">
                      {bindings.length === 0 && !templatesLoading ? (
                        <p className="text-sm text-gray-500">{t('common.loading')}</p>
                      ) : (
                        renderBindingRows(selectedSectionRows)
                      )}
                      {!canAdminister ? (
                        <p className="mt-2 text-xs text-gray-500">{t('pages.settings.readOnly')}</p>
                      ) : null}
                    </div>
                  </div>
                </section>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
